import { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { ChevronDown, User, LogOut } from 'lucide-react'
import { useAuthStore } from '@store/useAuthStore'
import { NotificationPanel } from '@components/notifications/NotificationPanel'

export function Header() {
  const { user, logout, role, getRole } = useAuthStore()
  const effectiveRole = role ?? (typeof getRole === 'function' ? getRole() : null)
  const navigate = useNavigate()
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  const roleLabel = (() => {
    if (effectiveRole === 'mechanic') return 'Mecânico'
    if (effectiveRole === 'administrator') return 'Administrador'
    return 'Rececionista'
  })()

  const handleLogoClick = () => {
    if (effectiveRole === 'mechanic') {
      navigate('/work-orders')
    } else {
      navigate('/homepage')
    }
  }

  const handleUserClick = () => {
    setIsMenuOpen(!isMenuOpen)
  }

  const handleLogout = () => {
    logout()
    navigate('/login')
    setIsMenuOpen(false)
  }

  // Fechar menu ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false)
      }
    }

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isMenuOpen])

  return (
    <header className="bg-[#17253C] text-white h-16 flex items-center justify-between w-full max-w-[100vw] px-3 sm:px-4 md:px-6 shadow-none md:shadow-lg">
      {/* Logo Section - Clickable */}
      <div 
        className="flex items-center space-x-2 sm:space-x-3 h-full cursor-pointer hover:opacity-80 transition-opacity flex-shrink-0"
        onClick={handleLogoClick}
      >
        <img
          src="/FIXDAUTO.png"
          alt="FIXDAUTO"
          className="max-h-10 sm:max-h-12 w-auto object-contain"
        />
      </div>

      {/* Right Section - Notifications, User (título removido - já visível no topo de cada página) */}
      <div className="flex items-center space-x-2 sm:space-x-4 relative flex-shrink-0 ml-auto">
        {/* Notifications */}
        <NotificationPanel />

        {/* User / Role - Clickable with Dropdown */}
        <div className="relative" ref={menuRef}>
          <button
            onClick={handleUserClick}
            className="flex items-center space-x-2 hover:bg-gray-700 rounded-lg px-2 py-1 transition-colors"
          >
            <span className="text-xs sm:text-sm font-medium truncate max-w-[80px] sm:max-w-none">{user?.name || roleLabel}</span>
            <ChevronDown className={isMenuOpen ? 'w-3 h-3 sm:w-4 sm:h-4 rotate-180 transition-transform' : 'w-3 h-3 sm:w-4 sm:h-4 transition-transform'} />
          </button>

          {/* Dropdown Menu */}
          {isMenuOpen && (
            <div className="absolute right-0 top-full mt-2 bg-white rounded-lg shadow-xl w-56 py-2 z-50 border border-gray-200">
              {/* Dados Pessoais */}
              <button
                className="w-full flex items-center space-x-3 px-4 py-3 hover:bg-gray-100 transition-colors text-left"
                onClick={() => {
                  navigate('/profile')
                  setIsMenuOpen(false)
                }}
              >
                <User className="w-5 h-5 text-gray-600" />
                <span className="font-medium text-gray-900">Dados pessoais</span>
              </button>

              {/* Logout */}
              <button
                className="w-full flex items-center space-x-3 px-4 py-3 hover:bg-red-50 transition-colors text-left"
                onClick={handleLogout}
              >
                <LogOut className="w-5 h-5 text-red-600" />
                <span className="font-medium text-red-600">Logout</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  )
}
